
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
  useToast,
  Text,
  Stack,
  Avatar,
  Flex,
  useColorModeValue,
  SimpleGrid,
  Skeleton,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { MdLock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../Context/userContext';
import { useAuth } from '../hooks/useAuth';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

export const EditProfilePage: React.FC = () => {
  const { user, refreshAll: refreshUser } = useUser();
  const { session, resetPassword } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    displayName: '',
  });

  // Theme colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  // Initialize form when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        displayName: user.displayName || '',
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = session?.access_token;
      if (!token) {
          throw new Error("No active session");
      }

      // Clean up empty strings to avoid validation errors (e.g. invalid phone format for "")
      const payload: Record<string, any> = {};
      Object.keys(formData).forEach(key => {
        const value = (formData as any)[key];
        if (value !== '' && value !== null && value !== undefined) {
          payload[key] = value;
        }
      });

      await apiJson('/api/auth/me', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }, token);

      // Refresh user context to show new values
      if (refreshUser) {
        await refreshUser();
      }

      toast({
        title: 'Profile updated',
        description: 'Your profile information has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Redirect to home as requested
      navigate('/home');
    } catch (error) {
      console.error('Update failed:', error);
      toast({
        title: 'Update failed',
        description: toUserMessage(error, "We couldn't update your profile. Please try again."),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setPasswordLoading(true);
    try {
      const result = await resetPassword("");
      
      if (result.success) {
        toast({
          title: 'Email sent',
          description: 'Check your inbox for the password reset link.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error || 'Failed to send reset email');
      }
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(error, "We couldn't send the password reset email. Please try again."),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return (
        <Container maxW="container.md" py={10}>
            <VStack spacing={6}>
                <Skeleton height="40px" width="200px" />
                <Skeleton height="200px" width="100%" />
            </VStack>
        </Container>
    );
  }

  return (
    <Container maxW="container.md" py={10}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading as="h1" size="xl" mb={2}>
            Edit Profile
          </Heading>
          <Text color={textColor}>
            Update your personal information.
          </Text>
        </Box>

        <Flex 
            direction={{ base: 'column', md: 'row' }} 
            gap={8} 
            bg={bgColor} 
            p={8} 
            borderRadius="xl" 
            boxShadow="sm"
            borderWidth="1px"
        >
            {/* Sidebar / Avatar Section */}
            <VStack spacing={4} align="center" minW="200px">
                <Avatar 
                    size="2xl" 
                    name={user.displayName || user.email} 
                    src={user.avatarUrl || undefined}
                    bg="brand.primary"
                    color="white" 
                />
                <Text fontWeight="bold" fontSize="lg">{user.displayName || user.email}</Text>
                <Text fontSize="sm" color="gray.500">{user.email}</Text>
            </VStack>

            {/* Form Section */}
            <Box flex={1} as="form" onSubmit={handleSubmit}>
                <VStack spacing={5}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} w="full">
                        <FormControl>
                            <FormLabel>First Name</FormLabel>
                            <Input 
                                name="firstName" 
                                value={formData.firstName} 
                                onChange={handleChange} 
                                placeholder="Jane"
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Last Name</FormLabel>
                            <Input 
                                name="lastName" 
                                value={formData.lastName} 
                                onChange={handleChange} 
                                placeholder="Doe"
                            />
                        </FormControl>
                    </SimpleGrid>

                    <FormControl>
                        <FormLabel>Display Name</FormLabel>
                        <Input 
                            name="displayName" 
                            value={formData.displayName} 
                            onChange={handleChange} 
                            placeholder="How your name appears to others"
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Phone Number</FormLabel>
                        <Input 
                            name="phoneNumber" 
                            value={formData.phoneNumber} 
                            onChange={handleChange} 
                            placeholder="+15550000000"
                        />
                         <Text fontSize="xs" color="gray.500" mt={1}>
                            E.164 format (e.g. +1...)
                        </Text>
                    </FormControl>

                    <Stack direction="row" spacing={4} pt={4} w="full" justify="flex-end">
                        <Button variant="ghost" onClick={() => navigate(-1)}>
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            colorScheme="blue" 
                            isLoading={loading}
                            bg="brand.primary"
                            _hover={{ bg: "brand.primaryDark" }}
                            color="white"
                        >
                            Save Changes
                        </Button>
                    </Stack>
                </VStack>
            </Box>
        </Flex>

        {/* Password Reset Section */}
        <Box
          bg={bgColor}
          p={6}
          borderRadius="xl"
          boxShadow="sm"
          borderWidth="1px"
        >
          <Flex align="center" gap={3} mb={3}>
            <Icon as={MdLock} boxSize={5} color="gray.500" />
            <Heading as="h3" size="md">
              Change Password
            </Heading>
          </Flex>
          <Text color={textColor} mb={4}>
            We'll send a password reset link to your email address.
          </Text>
          <Button
            variant="outline"
            colorScheme="blue"
            onClick={handlePasswordReset}
            isLoading={passwordLoading}
            loadingText="Sending..."
          >
            Send Reset Email
          </Button>
        </Box>
      </VStack>
    </Container>
  );
};

export default EditProfilePage;

